import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category } from '@/types'

import Link from 'next/link'
import { Layers } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/generals/empty-state'
import { SectionHeading } from '@/components/generals/section-heading'
import { CategoryCard } from '@/components/wallpapers/category-card'
import { buttonVariants } from '@/components/ui/button'
import { pluralize } from '@/lib/format'
import { getCategories } from '@/lib/wallpapers'
import { REVALIDATE_SECONDS } from '@/constants/site'

interface CategoriesPageProps {
    categories: Array<Category>
}

const CategoriesPage: FC<CategoriesPageProps> = ({ categories }): ReactNode => {
    const total = categories.reduce((sum, category) => sum + category.wallpaper_count, 0)

    return (
        <Layout>
            <Seo
                title='Categories'
                description='Explore wallpaper categories, from snowy mountains and turquoise oceans to neon cities and abstract art.'
                ogImage={categories[0]?.cover_image ?? undefined}
            />

            <section id='categories-header' className='pt-10 sm:pt-14'>
                <Container>
                    <SectionHeading
                        as='h1'
                        eyebrow='Categories'
                        title={(
                            <>
                                Every mood,
                                {' '}
                                <em className='text-brand'>one place</em>
                            </>
                        )}
                        description={categories.length > 0 ? (
                            <p>
                                <span>{categories.length}</span>
                                {' '}
                                {pluralize(categories.length, 'collection')}
                                {' and '}
                                <span>{total}</span>
                                {' '}
                                {pluralize(total, 'wallpaper')}
                                {', each one picked to look good behind your apps and icons.'}
                            </p>
                        ) : undefined}
                    />
                </Container>
            </section>

            <section id='categories' aria-label='All categories' className='pt-10 sm:pt-12'>
                <Container>
                    {categories.length > 0 ? (
                        <ul className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                            {categories.map(category => (
                                <li key={category.id}>
                                    <CategoryCard
                                        category={category}
                                        showDescription
                                        sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState
                            icon={<Layers aria-hidden='true' className='size-5' />}
                            title='Categories are on the way'
                            description='New collections are being put together. In the meantime, browse everything in one place.'
                            action={(
                                <Link href='/wallpapers' className={buttonVariants({ size: 'lg' })}>
                                    Browse wallpapers
                                </Link>
                            )}
                        />
                    )}
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<CategoriesPageProps> = async () => {
    const categories = await getCategories()

    return {
        props: {
            categories: categories.filter(category => category.wallpaper_count > 0)
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default CategoriesPage
